import type { PrecoCalculado } from '@azoupvendas/shared';

// AIDEV-NOTE: Tipos locais para os dados que o PricingService recebe.
// Esses tipos refletem a resposta da API /api/produtos (campos extras alem do Produto base).

export interface ProdutoPricing {
  CODIGO: number;
  PRECO_VENDA: number;
  PRECO_ATACADO: number;
  preco_promocional?: number | null;
  promo_inicio?: string | null;
  promo_fim?: string | null;
  precos_por_tabela?: Record<number, number> | null;
  quantidade_atacado?: number | null;
}

export interface ClientePricing {
  CODIGO: number;
  preco_varejo_atacado?: 'V' | 'A';
}

export interface VendedorPricing {
  pode_selecionar_tabela: boolean;
  tabela_preco_padrao: number | null;
  tipo_preco_base: 'V' | 'A';
  tem_regras_quantidade: boolean;
}

/**
 * PricingService - Calcula o preco unitario de um produto
 * seguindo 6 niveis de precedencia.
 *
 * AIDEV-NOTE: Override do cliente tem precedencia maxima.
 * Promocao so aplica apos verificar override.
 *
 * Ordem de precedencia:
 * 1. Override cliente: preco_varejo_atacado === 'V' -> PRECO_VENDA
 * 2. Override cliente: preco_varejo_atacado === 'A' -> PRECO_ATACADO
 * 3. Promocao ativa (dentro do periodo)
 * 4. Modo 1: vendedor seleciona tabela -> precos_por_tabela[tabelaSelecionada]
 *    OU Modo 2: vendedor tem tabela padrao -> precos_por_tabela[tabela_padrao]
 * 5. Config global: tipo_preco_base — quando NAO ha regras_tabela_preco_quantidade
 * 6. Modo 3: quantidade >= quantidade_atacado — quando HA regras_tabela_preco_quantidade
 */
export class PricingService {
  calcularPreco(
    produto: ProdutoPricing,
    cliente: ClientePricing,
    vendedor: VendedorPricing,
    tabelaSelecionada: number | null,
    quantidade: number
  ): PrecoCalculado {
    // AIDEV-NOTE: Precedencia 1 e 2 - Override do cliente
    if (cliente.preco_varejo_atacado === 'V') {
      const preco = produto.PRECO_VENDA;
      return {
        preco_unitario: preco,
        subtotal: preco * quantidade,
        modo_aplicado: 'varejo',
        is_promocional: false
      };
    }

    if (cliente.preco_varejo_atacado === 'A') {
      const preco = produto.PRECO_ATACADO;
      return {
        preco_unitario: preco,
        subtotal: preco * quantidade,
        modo_aplicado: 'atacado',
        is_promocional: false
      };
    }

    // AIDEV-NOTE: Precedencia 3 - Promocao ativa
    if (this.isPromocaoAtiva(produto)) {
      const preco = produto.preco_promocional!;
      return {
        preco_unitario: preco,
        subtotal: preco * quantidade,
        modo_aplicado: 'promocional',
        is_promocional: true
      };
    }

    // AIDEV-NOTE: Precedencia 4 - Modo 1: vendedor seleciona tabela
    if (vendedor.pode_selecionar_tabela && tabelaSelecionada != null) {
      const precoTabela = produto.precos_por_tabela?.[tabelaSelecionada];
      if (precoTabela != null && precoTabela > 0) {
        return {
          preco_unitario: precoTabela,
          subtotal: precoTabela * quantidade,
          modo_aplicado: 'tabela',
          is_promocional: false
        };
      }
    }

    // AIDEV-NOTE: Precedencia 5 - Modo 2: tabela padrao do vendedor
    if (!vendedor.pode_selecionar_tabela && vendedor.tabela_preco_padrao != null) {
      const precoTabela = produto.precos_por_tabela?.[vendedor.tabela_preco_padrao];
      if (precoTabela != null && precoTabela > 0) {
        return {
          preco_unitario: precoTabela,
          subtotal: precoTabela * quantidade,
          modo_aplicado: 'tabela',
          is_promocional: false
        };
      }
    }

    // AIDEV-NOTE: Precedencia 5 - Config global tipo_preco_base (sem regras de quantidade)
    // Só aplica quando nao ha regras_tabela_preco_quantidade configuradas para o cliente.
    if (!vendedor.tem_regras_quantidade) {
      const preco = vendedor.tipo_preco_base === 'A' ? produto.PRECO_ATACADO : produto.PRECO_VENDA;
      return {
        preco_unitario: preco,
        subtotal: preco * quantidade,
        modo_aplicado: vendedor.tipo_preco_base === 'A' ? 'atacado' : 'varejo',
        is_promocional: false
      };
    }

    // AIDEV-NOTE: Precedencia 6 - Modo 3: automatico por quantidade (quando ha regras de qtd)
    const qtdAtacado = produto.quantidade_atacado ?? 10;
    if (quantidade >= qtdAtacado) {
      const preco = produto.PRECO_ATACADO;
      return {
        preco_unitario: preco,
        subtotal: preco * quantidade,
        modo_aplicado: 'atacado',
        is_promocional: false
      };
    }

    // Fallback final: preco varejo
    const preco = produto.PRECO_VENDA;
    return {
      preco_unitario: preco,
      subtotal: preco * quantidade,
      modo_aplicado: 'varejo',
      is_promocional: false
    };
  }

  /**
   * Verifica se o produto tem promocao ativa no momento.
   */
  isPromocaoAtiva(produto: ProdutoPricing): boolean {
    if (!produto.preco_promocional || produto.preco_promocional <= 0) {
      return false;
    }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    if (produto.promo_inicio) {
      const inicio = new Date(produto.promo_inicio);
      inicio.setHours(0, 0, 0, 0);
      if (hoje < inicio) return false;
    }

    if (produto.promo_fim) {
      const fim = new Date(produto.promo_fim);
      fim.setHours(23, 59, 59, 999);
      if (hoje > fim) return false;
    }

    return true;
  }
}

// AIDEV-NOTE: Singleton para evitar recriacao desnecessaria
export const pricingService = new PricingService();
